import React, { useState, useEffect } from 'react';
import { ChevronDown, Search, Building2 } from 'lucide-react';
import { providerService } from '../../../services/outageService';

const ProviderSelector = ({ selectedProvider, onProviderChange, error }) => {
  const [providers, setProviders] = useState([]);
  const [filteredProviders, setFilteredProviders] = useState([]);
  const [isOpen, setIsOpen] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedCategory, setSelectedCategory] = useState('all');
  const [loading, setLoading] = useState(true);
  const [fetchError, setFetchError] = useState('');

  const categories = [
    { value: 'all', label: 'All Categories' },
    { value: 'internet', label: 'Internet' },
    { value: 'mobile', label: 'Mobile' },
    { value: 'cable_tv', label: 'Cable TV' },
    { value: 'streaming', label: 'Streaming' },
    { value: 'gaming', label: 'Gaming' },
    { value: 'cloud_services', label: 'Cloud Services' },
    { value: 'banking', label: 'Banking' },
    { value: 'social_media', label: 'Social Media' },
    { value: 'e_commerce', label: 'E-commerce' },
    { value: 'government', label: 'Government' }
  ];

  useEffect(() => {
    loadProviders();
  }, []);

  useEffect(() => {
    filterProviders();
  }, [providers, searchTerm, selectedCategory]);

  const loadProviders = async () => {
    setLoading(true);
    const { data, error } = await providerService?.getAllProviders();
    
    if (error) {
      setFetchError(error?.message);
    } else {
      setProviders(data || []);
    }
    setLoading(false);
  };

  const filterProviders = () => {
    let filtered = providers;

    // Filter by category
    if (selectedCategory !== 'all') {
      filtered = filtered?.filter(provider => provider?.category === selectedCategory);
    }

    // Filter by search term
    if (searchTerm) {
      filtered = filtered?.filter(provider =>
        provider?.name?.toLowerCase()?.includes(searchTerm?.toLowerCase()) ||
        provider?.description?.toLowerCase()?.includes(searchTerm?.toLowerCase())
      );
    }

    setFilteredProviders(filtered);
  };

  const handleProviderSelect = (provider) => {
    onProviderChange(provider);
    setIsOpen(false);
    setSearchTerm('');
  };

  const handleCategoryChange = (category) => {
    setSelectedCategory(category);
  };

  const getCategoryIcon = (category) => {
    const iconMap = {
      internet: '🌐',
      mobile: '📱',
      cable_tv: '📺',
      streaming: '🎬',
      gaming: '🎮',
      cloud_services: '☁️',
      banking: '🏦',
      social_media: '💬',
      e_commerce: '🛒',
      government: '🏛️'
    };
    return iconMap?.[category] || '📋';
  };

  if (loading) {
    return (
      <div className="space-y-2">
        <label className="block text-sm font-medium text-gray-700">
          Service Provider
        </label>
        <div className="animate-pulse">
          <div className="h-12 bg-gray-200 rounded-md"></div>
        </div>
      </div>
    );
  }

  if (fetchError) {
    return (
      <div className="space-y-2">
        <label className="block text-sm font-medium text-gray-700">
          Service Provider
        </label>
        <div className="p-3 text-sm text-red-600 bg-red-50 border border-red-200 rounded-md">
          Failed to load providers: {fetchError}
          <button 
            onClick={loadProviders}
            className="ml-2 text-red-700 hover:text-red-800 underline"
          >
            Try Again
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-2">
      <label className="block text-sm font-medium text-gray-700">
        Service Provider *
      </label>
      <div className="relative">
        <button
          type="button"
          onClick={() => setIsOpen(!isOpen)}
          className={`relative w-full bg-white border ${error ? 'border-red-300' : 'border-gray-300'} rounded-md py-3 pl-3 pr-10 text-left cursor-pointer focus:outline-none focus:ring-1 focus:ring-blue-500 focus:border-blue-500`}
        >
          {selectedProvider ? (
            <div className="flex items-center space-x-3">
              <span className="text-lg">{getCategoryIcon(selectedProvider?.category)}</span>
              <div>
                <div className="font-medium text-gray-900">{selectedProvider?.name}</div>
                <div className="text-sm text-gray-500 capitalize">
                  {selectedProvider?.category?.replace('_', ' ')}
                </div>
              </div>
            </div>
          ) : (
            <span className="text-gray-500">Select a service provider</span>
          )}
          <span className="absolute inset-y-0 right-0 flex items-center pr-2">
            <ChevronDown className="h-5 w-5 text-gray-400" />
          </span>
        </button>

        {isOpen && (
          <div className="absolute z-10 mt-1 w-full bg-white shadow-lg max-h-96 rounded-md py-1 text-base ring-1 ring-black ring-opacity-5 overflow-auto focus:outline-none">
            {/* Search and Category Filter */}
            <div className="sticky top-0 bg-white border-b border-gray-200 p-3 space-y-3">
              <div className="relative">
                <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-gray-400" />
                <input
                  type="text"
                  placeholder="Search providers..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e?.target?.value)}
                  className="w-full pl-10 pr-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-1 focus:ring-blue-500 focus:border-blue-500"
                />
              </div>
              
              <select
                value={selectedCategory}
                onChange={(e) => handleCategoryChange(e?.target?.value)}
                className="w-full py-2 px-3 border border-gray-300 rounded-md focus:outline-none focus:ring-1 focus:ring-blue-500 focus:border-blue-500"
              >
                {categories?.map(category => (
                  <option key={category?.value} value={category?.value}>
                    {category?.label}
                  </option>
                ))}
              </select>
            </div>

            {/* Provider List */}
            <div className="max-h-64 overflow-y-auto">
              {filteredProviders?.length === 0 ? (
                <div className="px-3 py-8 text-center text-gray-500">
                  <Building2 className="w-8 h-8 mx-auto mb-2 text-gray-400" />
                  <p>No providers found</p>
                  {searchTerm && (
                    <p className="text-sm">Try a different search term</p>
                  )}
                </div>
              ) : (
                filteredProviders?.map((provider) => (
                  <div
                    key={provider?.id}
                    onClick={() => handleProviderSelect(provider)}
                    className="cursor-pointer select-none relative py-3 px-3 hover:bg-gray-50"
                  >
                    <div className="flex items-center space-x-3">
                      <span className="text-lg">{getCategoryIcon(provider?.category)}</span>
                      <div className="flex-1">
                        <div className="flex items-center justify-between">
                          <span className="font-medium text-gray-900">{provider?.name}</span>
                          <span className="text-xs text-gray-500 capitalize">
                            {provider?.category?.replace('_', ' ')}
                          </span>
                        </div>
                        {provider?.description && (
                          <p className="text-sm text-gray-500 truncate">
                            {provider?.description}
                          </p>
                        )}
                      </div>
                    </div>
                  </div>
                ))
              )}
            </div>
          </div>
        )}
      </div>
      {error && (
        <p className="text-sm text-red-600">{error}</p>
      )}
    </div>
  );
};

export default ProviderSelector;